import { createSelector } from 'reselect';
import { fromJS } from 'immutable';

import { getCredentials, getCredentialGroups } from 'selectors/entities/credentialSelectors';
import {
  getProjectById,
  getServicesMap,
  getProjectImportantPages,
  getServiceHistory,
} from 'selectors/entities/projectsSelectors';
import { getServices } from 'selectors/entities/servicesSelectors';
import { getAllUsers } from 'selectors/entities/userSelectors';
import { getSearch } from 'selectors/settingsSelectors';

import { getUserFullName } from 'helpers/userHelper';
import { getFiles } from 'selectors/entities/fileSelector';

// Return project task list filters
export const getProjectTaskFilters = (state, params = {}) =>
  state.getIn(['settings', 'userState', 'projectTaskList', 'id-' + params.projectId, 'filters']);

// Return project task board filters
export const getProjectTaskBoardFilters = (state, params = {}) =>
  state.getIn(['settings', 'userState', 'projectTaskBoard', 'id-' + params.projectId, 'filters']);

// Return project task board filters
export const getProjectTaskBoardSorting = (state, params = {}) =>
  state.getIn(['settings', 'userState', 'projectTaskBoard', 'id-' + params.projectId, 'sorting']);

// Return project task list filters
export const getProjectTaskSorting = (state, params = {}) =>
  state.getIn(['settings', 'userState', 'projectTaskList', 'id-' + params.projectId, 'sorting']);

// Return project credential page filters
export const getProjectCredentialsFilters = (state, params = {}) => {
  let filters = state.getIn(['ui', 'projectView', 'credentials', 'filters']);
  if (params.projectId && filters?.get('projectId') !== params.projectId) {
    filters = filters.set('filter', 'all').set('category', 'all');
  }
  return filters;
};

// Return project credential page pagination
export const getProjectCredentialsPagination = (state) =>
  state.getIn(['ui', 'projectView', 'credentials', 'pagination']);

// Return project credential page sidebar collapsed
export const getProjectCredentialsSidebar = (state) =>
  state.getIn(['ui', 'projectView', 'credentials', 'sidebarCollapsed']);

// Return project credential page dataset credentials
export const getProjectCredentialsDataset = (state) => state.getIn(['ui', 'projectView', 'credentials', 'dataset']);

// Return project service page form service map
export const getServiceMapForm = (state) => state.getIn(['ui', 'projectView', 'serviceMaps', 'form']);

// Return project service page view service map
export const getServiceMapView = (state) => state.getIn(['ui', 'projectView', 'serviceMaps', 'view']);

// Return project tasks page filters
export const getProjectTasksFilters = (state) => state.getIn(['ui', 'projectView', 'tasks', 'filters']);

export const getProjectImportantPagesPagination = (state) =>
  state.getIn(['ui', 'projectView', 'importantPages', 'pagination']);

export const getProjectImportantPagesDataset = (state) =>
  state.getIn(['ui', 'projectView', 'importantPages', 'dataset']);

export const getImportantPagesByDataset = createSelector(
  [getProjectImportantPages, getProjectImportantPagesDataset],
  (pages, dataset) => {
    return dataset.map((pageId) => pages.get(pageId));
  },
);

// Returns credentials contained in the credential list dataset
export const getProjectCredentialsByDataset = createSelector(
  [getCredentials, getProjectCredentialsDataset],
  (credentials, dataset) => {
    return dataset.map((accessId) => credentials.get(accessId)).filter((credential) => credential);
  },
);

// Returns credentials with related models info
export const getProjectCredentialsWithSummary = createSelector(
  [getProjectCredentialsByDataset, getCredentialGroups],
  (credentials, groups) => {
    if (credentials && groups) {
      return credentials.map((credential) => {
        const group = groups ? groups.get(credential.get('groupId')) : null;

        return credential.merge(
          fromJS({
            groupName: group ? group.get('name') : null,
          }),
        );
      });
    }
    return null;
  },
);

// Return service maps by project id
export const getServiceMapsByProjectId = createSelector(
  [getServicesMap, getServices, getProjectById, getAllUsers, getSearch, getServiceHistory],
  (serviceMaps, services, project, users, search, serviceHistory) => {
    if (serviceMaps && services && project) {
      serviceMaps = serviceMaps
        .filter((serviceMap) => serviceMap.get('projectId') === project.get('projectId'))
        .map((serviceMap) => {
          const user = users.get(serviceMap.get('userId'));
          const histories = serviceHistory
            .filter((item) => item.get('relationId') === serviceMap.get('relationId'))
            .map((item) => {
              let user = users.get(item.get('userId'));
              return item.set('user', user ? getUserFullName(user) : null);
            });
          return serviceMap.merge(
            fromJS({
              histories,
              service: services.get(serviceMap.get('serviceId')),
              userFullName: user ? getUserFullName(user) : null,
            }),
          );
        });
      return serviceMaps.filter((serviceMap) => {
        if (!search) {
          return true;
        } else {
          const fullHeader = serviceMap.get('userFullName') + serviceMap.getIn(['service', 'name']);

          return fullHeader?.toLowerCase()?.indexOf(search.toString()) !== -1;
        }
      });
    }
    return null;
  },
);

export const getProjectFilesPagination = (state) => state.getIn(['ui', 'projectView', 'projectFiles', 'pagination']);

export const getProjectFilesDataset = (state) => state.getIn(['ui', 'projectView', 'projectFiles', 'dataset']);

export const getProjectFilesByDataset = createSelector([getProjectFilesDataset, getFiles], (dataset, files) => {
  return dataset.map((fileId) => files.get(fileId));
});
